import { Observable } from 'rxjs';

import { Overlay, OverlayRef, OverlayConfig } from '@angular/cdk/overlay';
import {
  ComponentType,
  TemplatePortal,
  PortalInjector,
  ComponentPortal,
} from '@angular/cdk/portal';
import { Injector, OnDestroy, Injectable, TemplateRef } from '@angular/core';

import { SnScreenService } from '@lib/common/services';

import { SnDrawerContainer } from '../containers';
import { ISnDrawerAppContentMargin } from '../interfaces';
import { SnDrawerConfig, SnDrawerData } from '../models';
import { SN_DRAWER_DATA } from '../providers';
import { SnDrawerRef } from '../references';
import { SnDrawerStore } from './drawer.store';

@Injectable()
export class SnDrawerService implements OnDestroy {
  constructor(
    private readonly _overlay: Overlay,
    private readonly _screen: SnScreenService,
    private readonly _injector: Injector,
    private readonly _store: SnDrawerStore,
  ) {}

  get drawers(): ReadonlyMap<string, SnDrawerRef> {
    return this._store.drawers;
  }

  get drawerAppContentMargins(): ISnDrawerAppContentMargin {
    return this._store.appContentMargins;
  }

  get appContentMarginChange$(): Observable<ISnDrawerAppContentMargin> {
    return this._store.appContentMarginChange$;
  }

  public ngOnDestroy(): void {}

  public open<T = any, D = any, R = any>(
    componentOrTemplateRef: ComponentType<T> | TemplateRef<T>,
    config?: SnDrawerConfig<D>,
  ): SnDrawerRef<T, D, R> {
    const drawerConfig = new SnDrawerConfig(config);

    if (config.id && this.has(config.id)) {
      throw Error(`Drawer with id "${config.id}" exists already. The drawer id must be unique.`);
    }

    const overlayRef = this._createOverlay(drawerConfig);
    const drawerContainer = this._attachContainer(overlayRef, drawerConfig);

    const drawerData = SnDrawerData.createWithProxy(drawerConfig.data);
    const drawerRef = new SnDrawerRef<T, D, R>(
      drawerConfig.id,
      overlayRef,
      this._screen,
      drawerContainer,
      drawerData,
      drawerConfig,
    );

    this._attachContent(
      componentOrTemplateRef,
      drawerContainer,
      drawerRef,
      drawerData,
      drawerConfig,
    );

    this._store.set(drawerRef.id, drawerRef);
    drawerRef.close$.subscribe(() => {
      this._store.delete(drawerRef.id);
    });
    drawerRef.afterClosed$.subscribe(() => {
      overlayRef.dispose();
      this._store.delete(drawerRef.id);
    });

    if (drawerConfig.hasBackdrop) {
      drawerRef.backdropClick$.subscribe(() => {
        if (!drawerConfig.disableClose) {
          drawerRef.close();
        }
      });
    }

    return drawerRef;
  }

  public has(id: string): boolean {
    return this.drawers.has(id);
  }

  public get<T = any, R = any>(id: string): SnDrawerRef<T, R> {
    return this.drawers.get(id);
  }

  public close(name: string): void {
    const drawer = this.drawers.get(name);

    if (drawer) {
      drawer.close();
    }
  }

  public closeAll(): void {
    this.drawers.forEach((drawer) => {
      if (drawer.isModal) {
        return;
      }
      if (!drawer.isModal) {
        drawer.close();
      }
    });
  }

  private _createOverlay(drawerConfig: SnDrawerConfig): OverlayRef {
    const positionStrategy = this._overlay.position().global();

    // FIXME Fix closing couple of drawers
    const overlayConfig = new OverlayConfig({
      scrollStrategy: this._overlay.scrollStrategies.reposition(),
      hasBackdrop: drawerConfig.hasBackdrop,
      // disposeOnNavigation: drawerConfig.closeOnNavigation,
      panelClass: drawerConfig.panelClass,
      positionStrategy,
    });

    if (drawerConfig.hasBackdrop) {
      overlayConfig.scrollStrategy = this._overlay.scrollStrategies.block();
    }

    if (drawerConfig.backdropClass) {
      overlayConfig.backdropClass = drawerConfig.backdropClass;
    }

    return this._overlay.create(overlayConfig);
  }

  private _attachContainer(overlayRef: OverlayRef, config: SnDrawerConfig): SnDrawerContainer {
    const injectionTokens = new WeakMap<any>([[SnDrawerConfig, config]]);
    const injector = new PortalInjector(this._injector, injectionTokens);

    const containerPortal = new ComponentPortal(SnDrawerContainer, undefined, injector);
    const containerRef = overlayRef.attach(containerPortal);

    return containerRef.instance;
  }

  private _attachContent<T>(
    componentOrTemplateRef: ComponentType<T> | TemplateRef<T>,
    drawerContainer: SnDrawerContainer,
    drawerRef: SnDrawerRef<T>,
    drawerData: SnDrawerData,
    config: SnDrawerConfig,
  ): SnDrawerRef<T> {
    if (componentOrTemplateRef instanceof TemplateRef) {
      const context: any = { $implicit: config.data };
      const templatePortal = new TemplatePortal(componentOrTemplateRef, undefined, context);

      drawerContainer.attachTemplatePortal(templatePortal);
    } else {
      const injectionTokens = new WeakMap<any>([
        [SN_DRAWER_DATA, drawerData],
        [SnDrawerRef, drawerRef],
      ]);
      const userInjector = config && config.viewContainerRef && config.viewContainerRef.injector;
      const injector = new PortalInjector(userInjector || this._injector, injectionTokens);

      const componentPortal = new ComponentPortal(componentOrTemplateRef, undefined, injector);
      const componentRef = drawerContainer.attachComponentPortal<T>(componentPortal);

      drawerRef.componentInstance = componentRef.instance;
    }

    return drawerRef;
  }
}
